import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import PixPayment from "../components/PixPayment";
import { PartnerCompanyForm } from "../components/PartnerCompanyForm";
import { brl } from "../components/PurchaseDialog";
import { Handshake, Plus, Pencil } from "lucide-react";

const CLS = { awaiting_fee: "text-orange-500 border-orange-500/40", pending: "text-amber-500 border-amber-500/40",
              approved: "text-emerald-500 border-emerald-500/40", rejected: "text-rose-500 border-rose-500/40" };

const Rules = ({ fee }) => (
  <Card className="p-6 gold-border mb-8" data-testid="partner-rules">
    <div className="grid md:grid-cols-3 gap-6 text-sm">
      <div><div className="font-display font-bold mb-1">1. Cadastre</div><p className="text-muted-foreground">Taxa única de <b className="text-foreground">{brl(fee)}</b> via Pix ou saldo. Desconto mínimo de 3%. Se o cadastro for rejeitado, a taxa volta para seu saldo.</p></div>
      <div><div className="font-display font-bold mb-1">2. Aprovação</div><p className="text-muted-foreground">O cadastro fica <b className="text-foreground">Aguardando aprovação</b> até o admin liberar. Aprovado, aparece na vitrine de Parceiros.</p></div>
      <div><div className="font-display font-bold mb-1">3. Divisão do desconto</div><p className="text-muted-foreground">Ex.: desconto de 10% → 1% Conecta Mais, 1% para o patrocinador que indicou o cliente ao clube e 8% de volta para o cliente que comprou. O valor da venda (preço menos o desconto) entra no seu saldo quando o comprador valida (ou o admin libera).</p></div>
    </div>
  </Card>
);

const FeePay = ({ c, fee, onDone }) => {
  const [pix, setPix] = useState(null);
  const pay = async (method) => {
    if (method === "balance" && !window.confirm(`Pagar a taxa de ${brl(fee)} com seu saldo disponível?`)) return;
    try {
      const { data } = await api.post(`/my/partner-companies/${c.id}/pay-fee`, { method });
      if (data.pix) { setPix(data.pix); return; }
      toast.success("Taxa paga! Cadastro enviado para aprovação."); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (pix) return <div className="mt-3"><PixPayment initial={pix} onPaid={() => { setPix(null); toast.success("Taxa paga!"); onDone(); }} /></div>;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Button size="sm" className="gold-btn" onClick={() => pay("balance")} data-testid={`partner-fee-balance-${c.id}`}>Pagar {brl(fee)} com saldo</Button>
      <Button size="sm" variant="outline" onClick={() => pay("pix")} data-testid={`partner-fee-pix-${c.id}`}>Gerar Pix de {brl(fee)}</Button>
    </div>
  );
};

const DescEdit = ({ c, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(c.description);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/my/partner-companies/${c.id}/description`, { description: text }); toast.success("Descrição atualizada"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" onClick={() => { setText(c.description); setOpen(true); }} data-testid={`my-company-desc-edit-${c.id}`}><Pencil className="h-3 w-3 mr-1" /> Editar descrição</Button>;
  return (
    <div className="w-full mt-3 space-y-2" data-testid={`my-company-desc-form-${c.id}`}>
      <Textarea rows={2} maxLength={150} value={text} onChange={(e) => setText(e.target.value)} data-testid={`my-company-desc-input-${c.id}`} />
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{text.length}/150 caracteres · publicado na hora, sem nova aprovação</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button size="sm" className="gold-btn" disabled={busy || text.trim().length < 2} onClick={save} data-testid={`my-company-desc-save-${c.id}`}>Salvar</Button>
        </div>
      </div>
    </div>
  );
};

const CompanyRow = ({ c, fee, onEdit, onDone }) => (
  <Card className="p-4" data-testid={`my-company-${c.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-medium flex flex-wrap items-center gap-2">{c.name}
          <Badge variant="outline" className={CLS[c.status]} data-testid={`my-company-status-${c.id}`}>{c.status_label}</Badge>
          {c.fee_refunded && <Badge variant="outline">Taxa devolvida</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">{c.service_type} · {c.state} · desconto {c.discount_percent}% (comprador recebe {c.buyer_percent}%)</div>
        <div className="text-xs mt-1" data-testid={`my-company-desc-${c.id}`}>{c.description}</div>
        {c.reject_reason && <div className="text-xs text-rose-500 mt-1">Motivo: {c.reject_reason}</div>}
      </div>
      {["awaiting_fee", "pending"].includes(c.status) && (
        <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid={`my-company-edit-${c.id}`}><Pencil className="h-3 w-3 mr-1" /> Editar</Button>
      )}
      {c.status === "approved" && <DescEdit c={c} onDone={onDone} />}
    </div>
    {c.status === "awaiting_fee" && <FeePay c={c} fee={fee} onDone={onDone} />}
  </Card>
);

const Sales = ({ items }) => (
  <div className="divide-y divide-border/60 text-sm" data-testid="my-partner-sales">
    {items.map((s) => (
      <div key={s.id} className="py-2 flex flex-wrap justify-between gap-2">
        <span>#{s.code} · {s.company_name} · {s.buyer_name} <span className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString("pt-BR")}</span></span>
        <span className="font-mono">{brl(s.amount)} → você recebe {brl(s.split.company)} <Badge variant="outline">{s.status_label}</Badge></span>
      </div>
    ))}
  </div>
);

export default function BecomePartner() {
  const [list, setList] = useState(null);
  const [sales, setSales] = useState([]);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/my/partner-companies").then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/my/partner-sales").then((r) => setSales(r.data)).catch(() => {});
  }, []);
  useEffect(() => { api.get("/partner-companies").then((r) => setMeta(r.data)).catch(() => {}); load(); }, [load]);
  const fee = meta?.fee ?? 4.99;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2"><Handshake className="h-4 w-4" /> Parcerias</div>
        <div className="flex flex-wrap items-end justify-between gap-3 mt-1 mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Seja uma <span className="gold-text">Empresa Parceira</span></h1>
          <Button className="gold-btn" onClick={() => setEditing({})} data-testid="partner-company-new-btn"><Plus className="h-4 w-4 mr-2" /> Cadastrar empresa</Button>
        </div>
        <Rules fee={fee} />
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Meus cadastros</div>
        {!list ? <p className="text-muted-foreground">Carregando...</p> : list.length === 0
          ? <p className="text-sm text-muted-foreground" data-testid="my-companies-empty">Você ainda não cadastrou nenhuma empresa.</p>
          : <div className="space-y-3">{list.map((c) => <CompanyRow key={c.id} c={c} fee={fee} onEdit={setEditing} onDone={load} />)}</div>}
        {sales.length > 0 && (
          <Card className="p-6 mt-8"><div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Minhas vendas</div><Sales items={sales} /></Card>
        )}
      </div>
      {meta && <PartnerCompanyForm editing={editing} meta={meta} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
